import React from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import { useInternalCaseMessages } from '../../../data/support/useSupport';
import { SupportApi } from '../../../data/support/supportApi';
import { CaseMessageAttachment } from '../../../types/support.types';
import CaseMessage from './CaseMessage';

interface CaseThreadViewProps {
  caseId: string;
  threadId: string;
}

const CaseThreadView: React.FC<CaseThreadViewProps> = ({
  caseId,
  threadId,
}) => {
  const messagesQuery = useInternalCaseMessages(caseId, threadId, {
    polling: true,
  });
  const messages = messagesQuery.data || [];
  const bottomRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages.length]);

  const handleDownloadAttachment = async (
    messageId: string,
    attachment: CaseMessageAttachment,
  ) => {
    const result = await SupportApi.internalGetAttachmentUrl(
      caseId,
      messageId,
      attachment.id,
    );
    window.open(result.data.attributes.download_url, '_blank');
  };

  if (messagesQuery.isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          py: 4,
        }}
      >
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (!messages.length) {
    return (
      <Box sx={{ py: 4, textAlign: 'center' }}>
        <Typography color="text.secondary">No messages yet</Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        flex: 1,
        overflowY: 'auto',
        display: 'flex',
        flexDirection: 'column',
        py: 2,
        px: 1,
      }}
    >
      {messages.map((msg) => (
        <CaseMessage
          key={msg.id}
          message={msg}
          align={
            msg.author_type === 'system'
              ? 'center'
              : msg.author_type === 'internal'
                ? 'right'
                : 'left'
          }
          onDownloadAttachment={(att) => handleDownloadAttachment(msg.id, att)}
          getAttachmentUrl={async (att) => {
            const result = await SupportApi.internalGetAttachmentUrl(
              caseId,
              msg.id,
              att.id,
            );
            return result.data.attributes.download_url;
          }}
        />
      ))}
      <div ref={bottomRef} />
    </Box>
  );
};

export default CaseThreadView;
